import { WebBluetoothClient, type BluetoothOptions } from "@/lib/bluetooth";
import { NativeBluetoothClient } from "@/lib/nativeBluetooth";
import {
  isSupportedNativeParticipantPlatform,
  probeBluetoothTransportAvailability,
  subscribeBluetoothTransportAvailability,
  type BluetoothTransportProbe,
} from "@/lib/runtime";

type SensorState = {
  isSupported: boolean;
  isConnecting: boolean;
  isConnected: boolean;
  connectedDeviceName: string | null;
  supportMessage: string | null;
  error: string | null;
};

type SensorOptions = {
  bluetooth?: BluetoothOptions;
};

type StateListener = (state: SensorState) => void;
type LineListener = (line: string) => void;
type BluetoothClient = WebBluetoothClient | NativeBluetoothClient;

const AUTO_CONNECT_KEY = "imags_sensor_autoconnect";
const BLE_DEVICE_ID_KEY = "imags_sensor_ble_device_id";
const BLUETOOTH_RETRY_BASE_MS = 1000;
const BLUETOOTH_RETRY_MAX_MS = 16000;
const BLUETOOTH_UNSUPPORTED_MESSAGE =
  "Bluetooth transport is not supported on this device/runtime.";

const state: SensorState = {
  isSupported: false,
  isConnecting: false,
  isConnected: false,
  connectedDeviceName: null,
  supportMessage: null,
  error: null,
};

let initialized = false;
// Keep one shared client so route changes and multiple hooks observe the same BLE
// connection state instead of competing for separate device sessions.
let client: BluetoothClient | null = null;
let options: SensorOptions = {};
let autoConnectEnabled = false;
let autoConnectInFlight = false;
let bluetoothDeviceId: string | null = null;
let bluetoothAvailabilityUnsubscribe: (() => void) | null = null;
let bluetoothProbeVersion = 0;
let bluetoothReconnectTimer: number | null = null;
let bluetoothReconnectAttempts = 0;
let manualDisconnectInFlight = false;

const stateListeners = new Set<StateListener>();
const lineListeners = new Set<LineListener>();

function clearBluetoothReconnectTimer() {
  if (typeof window === "undefined") {
    bluetoothReconnectAttempts = 0;
    return;
  }
  if (bluetoothReconnectTimer != null) {
    window.clearTimeout(bluetoothReconnectTimer);
    bluetoothReconnectTimer = null;
  }
  bluetoothReconnectAttempts = 0;
}

function nextBluetoothReconnectDelayMs() {
  const delay = Math.min(
    BLUETOOTH_RETRY_BASE_MS * 2 ** Math.min(bluetoothReconnectAttempts, 4),
    BLUETOOTH_RETRY_MAX_MS
  );
  bluetoothReconnectAttempts += 1;
  return delay;
}

function scheduleBluetoothReconnect(_reason: string) {
  void _reason;
  if (typeof window === "undefined") return;
  if (!autoConnectEnabled) return;
  if (state.isConnected) return;
  if (bluetoothReconnectTimer != null) return;

  const delayMs = nextBluetoothReconnectDelayMs();

  bluetoothReconnectTimer = window.setTimeout(() => {
    bluetoothReconnectTimer = null;
    void (async () => {
      if (!autoConnectEnabled || state.isConnected) return;

      await autoConnectSensor();

      if (!autoConnectEnabled || state.isConnected) {
        clearBluetoothReconnectTimer();
        return;
      }

      scheduleBluetoothReconnect("retry");
    })();
  }, delayMs);
}

function setDisconnectedState(error: string | null) {
  state.isConnecting = false;
  state.isConnected = false;
  state.connectedDeviceName = null;
  state.error = error;
}

function handleBluetoothDisconnect() {
  if (manualDisconnectInFlight) return;

  setDisconnectedState("Bluetooth disconnected. Reconnecting...");
  notifyState();
  scheduleBluetoothReconnect("device-disconnected");
}

function errorMessage(err: unknown, fallback: string) {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

function normalizeSensorConnectError(err: unknown, fallback: string) {
  const message = errorMessage(err, fallback);
  const normalized = message.trim();
  const lowercase = normalized.toLowerCase();

  if (
    lowercase.includes("requestdevice() chooser") ||
    lowercase.includes("user cancelled") ||
    lowercase.includes("user canceled")
  ) {
    return "Sensor selection was cancelled.";
  }

  if (lowercase.includes("known bluetooth device not available")) {
    return "Saved sensor not available. Select the sensor again.";
  }

  return normalized || fallback;
}

function notifyState() {
  const snapshot = { ...state };
  for (const listener of stateListeners) {
    listener(snapshot);
  }
}

function notifyLine(line: string) {
  for (const listener of lineListeners) {
    listener(line);
  }
}

function clearBluetoothAvailabilitySubscription() {
  if (!bluetoothAvailabilityUnsubscribe) return;
  try {
    bluetoothAvailabilityUnsubscribe();
  } catch {
    // Ignore cleanup failures.
  }
  bluetoothAvailabilityUnsubscribe = null;
}

function bluetoothSupportMessageFromProbe(probe: BluetoothTransportProbe) {
  switch (probe.reason) {
    case "insecure_context":
      return "Bluetooth LE requires HTTPS (or 127.0.0.1).";
    case "blocked_by_permissions_policy":
      return "Web Bluetooth is blocked by this page's Permissions-Policy.";
    case "missing_web_bluetooth":
      return "Web Bluetooth API is not available in this browser.";
    case "bluetooth_unavailable":
      return "Bluetooth adapter appears unavailable. Turn on Bluetooth and try again.";
    default:
      return null;
  }
}

async function refreshBluetoothSupport() {
  if (typeof window === "undefined") return;
  if (isSupportedNativeParticipantPlatform()) return;

  // The probe is async and can race against reconnects or route changes.
  // Versioning lets the latest probe win without stale state writes.
  const version = ++bluetoothProbeVersion;
  const probe = await probeBluetoothTransportAvailability();
  if (version !== bluetoothProbeVersion) return;

  const previousSupportMessage = state.supportMessage;
  const nextSupported = probe.supported;
  const nextSupportMessage = bluetoothSupportMessageFromProbe(probe);
  const supportChanged =
    state.isSupported !== nextSupported ||
    state.supportMessage !== nextSupportMessage;

  state.isSupported = nextSupported;
  state.supportMessage = nextSupportMessage;

  if (supportChanged) {
    if (
      nextSupported &&
      ((previousSupportMessage && state.error === previousSupportMessage) ||
        state.error === BLUETOOTH_UNSUPPORTED_MESSAGE)
    ) {
      state.error = null;
    }
    notifyState();
  }
}

function loadAutoConnect() {
  if (typeof window === "undefined") return;
  autoConnectEnabled = window.sessionStorage.getItem(AUTO_CONNECT_KEY) === "1";
}

function loadBluetoothDeviceId() {
  if (typeof window === "undefined") return;
  bluetoothDeviceId = window.sessionStorage.getItem(BLE_DEVICE_ID_KEY);
}

function createBluetoothClient(): BluetoothClient {
  clearBluetoothAvailabilitySubscription();
  bluetoothProbeVersion += 1;
  clearBluetoothReconnectTimer();

  client =
    isSupportedNativeParticipantPlatform()
      ? new NativeBluetoothClient(notifyLine, handleBluetoothDisconnect)
      : new WebBluetoothClient(notifyLine, handleBluetoothDisconnect);

  state.isSupported = client.isSupported;
  setDisconnectedState(null);
  state.supportMessage = null;

  if (typeof window !== "undefined" && !isSupportedNativeParticipantPlatform()) {
    void refreshBluetoothSupport();
    bluetoothAvailabilityUnsubscribe = subscribeBluetoothTransportAvailability(() => {
      void refreshBluetoothSupport();
    });
  }

  notifyState();
  return client;
}

function ensureClient(): BluetoothClient {
  return client ?? createBluetoothClient();
}

function ensureInitialized() {
  if (initialized) return;
  loadAutoConnect();
  loadBluetoothDeviceId();
  createBluetoothClient();
  initialized = true;
}

function persistAutoConnect(enabled: boolean) {
  autoConnectEnabled = enabled;
  if (typeof window === "undefined") return;
  if (enabled) {
    window.sessionStorage.setItem(AUTO_CONNECT_KEY, "1");
  } else {
    window.sessionStorage.removeItem(AUTO_CONNECT_KEY);
  }
}

function persistBluetoothDeviceId(id: string | null) {
  bluetoothDeviceId = id;
  if (typeof window === "undefined") return;
  if (id) {
    window.sessionStorage.setItem(BLE_DEVICE_ID_KEY, id);
  } else {
    window.sessionStorage.removeItem(BLE_DEVICE_ID_KEY);
  }
}

function resolveBluetoothOptions(
  overrides: SensorOptions = {}
): BluetoothOptions {
  const bluetoothOptions = {
    ...options.bluetooth,
    ...overrides.bluetooth,
  };

  if (!bluetoothOptions.deviceId && bluetoothDeviceId) {
    bluetoothOptions.deviceId = bluetoothDeviceId;
    bluetoothOptions.allowDeviceChooserFallback = true;
  }

  return bluetoothOptions;
}

function applyConnectedState(bluetoothClient: BluetoothClient) {
  state.isConnected = true;
  state.isConnecting = false;
  state.error = null;
  state.connectedDeviceName = bluetoothClient.connectedDeviceName;
  persistAutoConnect(true);
  clearBluetoothReconnectTimer();

  const connectedId = bluetoothClient.connectedDeviceId;
  if (connectedId) {
    persistBluetoothDeviceId(connectedId);
  }
}

export function getSensorState(): SensorState {
  ensureInitialized();
  return { ...state };
}

export function subscribeSensorState(listener: StateListener): () => void {
  ensureInitialized();
  stateListeners.add(listener);
  listener({ ...state });
  return () => {
    stateListeners.delete(listener);
  };
}

export function subscribeSensorLines(listener: LineListener): () => void {
  ensureInitialized();
  lineListeners.add(listener);
  return () => {
    lineListeners.delete(listener);
  };
}

export function updateSensorOptions(next: SensorOptions) {
  options = {
    ...options,
    ...next,
  };
}

export async function connectSensor(overrides: SensorOptions = {}) {
  ensureInitialized();
  clearBluetoothReconnectTimer();
  if (state.isConnecting) return;
  const bluetoothClient = ensureClient();

  state.isConnecting = true;
  state.error = null;
  notifyState();

  if (!isSupportedNativeParticipantPlatform()) {
    await refreshBluetoothSupport();
  }

  if (!state.isSupported) {
    setDisconnectedState(state.supportMessage || BLUETOOTH_UNSUPPORTED_MESSAGE);
    notifyState();
    return;
  }

  try {
    await bluetoothClient.connect(resolveBluetoothOptions(overrides));
    applyConnectedState(bluetoothClient);
  } catch (err: unknown) {
    setDisconnectedState(
      normalizeSensorConnectError(
        err,
        "Failed to connect to Bluetooth sensor."
      )
    );
    console.warn("[SensorAutoConnect] connect failed", err);
  }

  notifyState();
}

export async function autoConnectSensor() {
  ensureInitialized();
  if (!autoConnectEnabled) return;
  if (state.isConnected) return;
  if (autoConnectInFlight) return;

  const bluetoothClient = ensureClient();

  autoConnectInFlight = true;
  try {
    if (!isSupportedNativeParticipantPlatform()) {
      await refreshBluetoothSupport();
    }

    const deviceId = bluetoothDeviceId;
    if (!deviceId || !bluetoothClient.isSupported || !state.isSupported) {
      return;
    }

    // Reconnect only to previously-approved devices. On the web, verify the
    // browser still exposes that known device before attempting to attach.
    if (!isSupportedNativeParticipantPlatform()) {
      const devices = await bluetoothClient.listDevices();
      const known = devices.find((device) => device.id === deviceId);
      if (!known) return;
    }

    await connectSensor({ bluetooth: { deviceId } });
    if (state.isConnected) {
      clearBluetoothReconnectTimer();
    }
  } finally {
    autoConnectInFlight = false;
  }
}

export async function disconnectSensor() {
  ensureInitialized();
  if (!client) return;
  clearBluetoothReconnectTimer();

  try {
    manualDisconnectInFlight = true;
    await client.disconnect();
  } finally {
    manualDisconnectInFlight = false;
    setDisconnectedState(null);
    persistAutoConnect(false);
    notifyState();
  }
}
